import AddIcon from "@mui/icons-material/Add";
import CleaningServicesOutlined from "@mui/icons-material/CleaningServicesOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { BackdropCustom } from "../../../components/backdrop/Backdrop";
import { PageCard } from "../../../components/pageCard/PageCard";
import { downloadBase64Zip } from "../../../utils/downloadPdf";
import { CustomMenuItem } from "../../../components/table/CustomMenuItem/CustomMenuItem";
import { CustomNoContentTableBody } from "../../../components/table/CustomNoContentTableBody/CustomNoContentTableBody";
import { CustomTableCell } from "../../../components/table/CustomTableCell/CustomTableCell";
import CustomTablePagination from "../../../components/table/CustomTablePagination/CustomTablePagination";
import CustomTableSortLabel from "../../../components/table/CustomTableSortLabel/CustomTableSortLabel";
import { FilterButtonGroup } from "../../../components/table/FilterButtonGroup/FilterButtonGroup";
import { GridFilterArea } from "../../../components/table/GridFilterArea/GridFilterArea";
import { TitleContainer } from "../../../components/titleContainer/TitleContainer";
import { useToastr } from "../../../components/toast/Toast";
import { useAuth } from "../../../hooks/auth";
import { IJudgment } from "../../../interfaces/IJudgment";
import { IListAllParams } from "../../../interfaces/IListAllParams";
import api from "../../../services/api";

const fields = [
  {
    field: "exhibitionName",
    label: "Nome da exposição",
  },
  {
    field: "categoryName",
    label: "Nome da Categoria",
  },
];

interface IJudgmentProps {
  exhibitionId: string;
  finished: boolean;
}

export const Judgments: React.FC<IJudgmentProps> = ({
  exhibitionId,
  finished,
}) => {
  const toastr = useToastr();
  const timeout = useRef<any>(null);
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const [judgments, setJudgments] = useState<IJudgment[]>([]);

  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState<number>(() => {
    const savedFilters = sessionStorage.getItem("@ORQUIDEAS:judgmentFilters");
    if (savedFilters) {
      const { perPage } = JSON.parse(savedFilters);

      return perPage || 10;
    }

    return 10;
  });
  const [currentPage, setCurrentPage] = useState<number>(() => {
    const savedFilters = sessionStorage.getItem("@ORQUIDEAS:judgmentFilters");
    if (savedFilters) {
      const { currentPage } = JSON.parse(savedFilters);

      return currentPage || 0;
    }

    return 0;
  });
  const [orderField, setOrderField] = useState<string>(() => {
    const savedFilters = sessionStorage.getItem("@ORQUIDEAS:judgmentFilters");
    if (savedFilters) {
      const { orderField } = JSON.parse(savedFilters);
      return orderField || "judgmentTypeName";
    }
    return "judgmentTypeName";
  });

  const [orderDirection, setOrderDirection] = useState<"asc" | "desc">(() => {
    const savedFilters = sessionStorage.getItem("@ORQUIDEAS:judgmentFilters");
    if (savedFilters) {
      const { orderDirection } = JSON.parse(savedFilters);
      return orderDirection || "asc";
    }
    return "asc";
  });
  const [loading, setLoading] = useState(false);
  const [certificateLoading, setCertificateLoading] = useState(false);

  const [gridFilterAreaOpen, setGridFilterAreaOpen] = useState(() => {
    const savedFilters = sessionStorage.getItem("@ORQUIDEAS:judgmentFilters");
    if (savedFilters) {
      const { gridFilterAreaOpen } = JSON.parse(savedFilters);

      return gridFilterAreaOpen;
    }
  });

  const [filterField, setFilterField] = useState<string>(() => {
    const savedFilters = sessionStorage.getItem("@ORQUIDEAS:judgmentFilters");
    if (savedFilters) {
      const { filterField } = JSON.parse(savedFilters);

      return filterField || "";
    }

    return "";
  });

  const [filterPrecision, setFilterPrecision] = useState<string>(() => {
    const savedFilters = sessionStorage.getItem("@ORQUIDEAS:judgmentFilters");
    if (savedFilters) {
      const { filterPrecision } = JSON.parse(savedFilters);

      return filterPrecision === "equal" ? "equal" : "containing";
    }

    return "containing";
  });

  const [filterValue, setFilterValue] = useState<string>(() => {
    const savedFilters = sessionStorage.getItem("@ORQUIDEAS:judgmentFilters");
    if (savedFilters) {
      const { filterValue } = JSON.parse(savedFilters);

      return filterValue || "";
    }

    return "";
  });

  const [delay, setDelay] = useState(0);

  const handleToJudgment = useCallback(
    (id: string | null) => {
      !!id
        ? navigate(`/judgment/${id}?exhibitionId=${exhibitionId}`)
        : navigate(`/judgment?exhibitionId=${exhibitionId}`);
    },
    [navigate, exhibitionId],
  );

  const handleGenerateCertificatesByJudgment = useCallback(
    async (judgmentId: string) => {
      if (!judgmentId) return;
      setCertificateLoading(true);
      try {
        const response = await api.get(`/judgments/${judgmentId}/certificates`);
        const { zip } = response.data as {
          zip: { filename: string; base64: string };
        };
        if (!zip) {
          toastr.error("Nenhum certificado de pódio encontrado para este julgamento.");
          return;
        }
        downloadBase64Zip(zip.base64, zip.filename);
      } catch (error: any) {
        const status = error?.response?.status;
        if (status === 422) {
          toastr.error("Nenhuma planta no pódio. Execute a consolidação antes de gerar certificados.");
        } else {
          toastr.error(error?.response?.data?.message || "Erro ao gerar certificados");
        }
      } finally {
        setCertificateLoading(false);
      }
    },
    [toastr],
  );

  const handleToPlants = useCallback(
    (judgmentId: string | null) => {
      navigate(`/plants?judgmentId=${judgmentId}&exhibitionId=${exhibitionId}`);
    },
    [navigate, exhibitionId],
  );

  const handleListAll = useCallback(
    async ({
      perPage,
      currentPage,
      orderField,
      orderDirection,
      field,
      value,
      precision,
      delay,
    }: IListAllParams) => {
      let url = `/judgments/all/${exhibitionId}?perPage=${perPage}&currentPage=${
        currentPage + 1
      }&orderBy=${orderField}&orderDirection=${orderDirection}`;

      if (field && value && precision) {
        url =
          url +
          `&filterField=${field}&filterValue=${value}&precision=${precision}`;
      }

      clearInterval(timeout.current);
      timeout.current = setTimeout(async () => {
        setLoading(true);
        await api
          .get(url)
          .then(respose => {
            const responseData = respose.data;

            if (responseData.data?.length > 0) {
              setJudgments(responseData.data);
              setTotalRows(responseData.totalRows);
              setLoading(false);
            } else {
              setJudgments([]);
              setTotalRows(0);
              setPerPage(10);
              setCurrentPage(0);
              setLoading(false);
            }
          })
          .catch(error => {
            if (error.response.status === 401) {
              signOut();
              navigate("/");
            }

            toastr.error(
              error?.response?.data?.message || "Erro ao listar categorias",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      }, delay || 0);
    },
    [
      exhibitionId,
      timeout,
      setLoading,
      setJudgments,
      setTotalRows,
      setPerPage,
      setCurrentPage,
      signOut,
      navigate,
      toastr,
    ],
  );

  const handleDeleteJudgment = useCallback(
    async (judgmentId: string) => {
      if (judgmentId) {
        setLoading(true);

        await api
          .delete(`/judgments/${judgmentId}`)
          .then(() => {
            handleListAll({
              perPage,
              currentPage,
              orderField,
              orderDirection,
              field: filterField,
              value: filterValue,
              precision: filterPrecision,
              delay: 0,
            });
            toastr.success("Julgamento excluído com sucesso");
          })
          .catch(error => {
            if (error.status === 401) {
              signOut();
              navigate("/");
            }

            toastr.error(
              error.response.data?.message || "Contate a equipe de suporte",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      }
    },
    [
      navigate,
      signOut,
      toastr,
      currentPage,
      filterField,
      filterPrecision,
      filterValue,
      handleListAll,
      orderDirection,
      orderField,
      perPage,
    ],
  );

  const handlePagination = async (
    perPage: number,
    currentPage: number,
    orderField: string,
    orderDirection: "asc" | "desc",
  ) => {
    handleListAll({
      perPage,
      currentPage,
      orderField,
      orderDirection,
      field: filterField,
      value: filterValue,
      precision: filterPrecision,
      delay: 0,
    });
  };

  useEffect(() => {
    const field = filterField;
    const value = filterValue;
    const precision = filterPrecision;

    handleListAll({
      perPage,
      currentPage,
      orderField,
      orderDirection,
      field,
      value,
      precision,
      delay,
    });
  }, [
    filterField,
    filterPrecision,
    filterValue,
    delay,
    currentPage,
    orderDirection,
    orderField,
    perPage,
    handleListAll,
  ]);

  const handleTextFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(e.target.value);
    setTotalRows(0);
    setCurrentPage(0);
    setDelay(500);
  };

  const saveFiltersToSessionStorage = useCallback(() => {
    const filters = {
      filterField,
      filterPrecision,
      filterValue,
      gridFilterAreaOpen,
      currentPage,
      perPage,
      orderField,
      orderDirection,
    };
    sessionStorage.setItem(
      "@ORQUIDEAS:judgmentFilters",
      JSON.stringify(filters),
    );
  }, [
    filterField,
    filterPrecision,
    filterValue,
    gridFilterAreaOpen,
    currentPage,
    perPage,
    orderField,
    orderDirection,
  ]);

  useEffect(() => {
    saveFiltersToSessionStorage();
  }, [
    filterField,
    filterPrecision,
    filterValue,
    gridFilterAreaOpen,
    currentPage,
    perPage,
    saveFiltersToSessionStorage,
  ]);

  const handleClearFilters = () => {
    setFilterField("");
    setFilterPrecision("containing");
    setFilterValue("");
    setCurrentPage(0);
    setPerPage(10);
    setOrderField("exhibitionName");
    setOrderDirection("asc");

    sessionStorage.removeItem("@ORQUIDEAS:judgmentFilters");
  };

  return (
    <>
      <TitleContainer>Categorias</TitleContainer>
      <PageCard>
        <FilterButtonGroup>
          <div>
            <Tooltip title="Filtros" placement="top">
              <IconButton
                onClick={() => setGridFilterAreaOpen(!gridFilterAreaOpen)}
                size="small"
                color="primary"
                style={{ alignItems: "flex-end" }}
              >
                {gridFilterAreaOpen ? (
                  <FilterAltOffOutlinedIcon />
                ) : (
                  <FilterAltOutlinedIcon />
                )}
              </IconButton>
            </Tooltip>

            {gridFilterAreaOpen && (
              <Tooltip title="Limpar Filtros" placement="top">
                <IconButton
                  onClick={() => handleClearFilters()}
                  size="small"
                  color="primary"
                  style={{ alignItems: "flex-end" }}
                >
                  <CleaningServicesOutlined />
                </IconButton>
              </Tooltip>
            )}
          </div>
          <Tooltip title="Criar julgamento" placement="top">
            <IconButton
              onClick={() => handleToJudgment(null)}
              size="small"
              color="primary"
              style={{ alignItems: "flex-end" }}
              disabled={finished}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        </FilterButtonGroup>

        {gridFilterAreaOpen && (
          <GridFilterArea container spacing={2}>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="select-filter">Filtrar por</InputLabel>
                <Select
                  label="Filtrar Por"
                  value={`${filterField}`}
                  onChange={e => {
                    setFilterField(`${e.target.value}`);
                  }}
                >
                  {fields.map(filter => {
                    return (
                      <MenuItem key={filter.field} value={filter.field}>
                        {filter.label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Precisão</InputLabel>
                <Select
                  label="Precisão"
                  value={`${filterPrecision}`}
                  onChange={e => {
                    setFilterPrecision(`${e.target.value}`);
                  }}
                >
                  <MenuItem key={"equal"} value={"equal"}>
                    Igual
                  </MenuItem>
                  <MenuItem key={"containing"} value={"containing"}>
                    Contendo
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Valor a filtrar"
                placeholder="Valor a filtrar"
                value={filterValue}
                onChange={handleTextFilterChange}
                size="small"
              />
            </Grid>
          </GridFilterArea>
        )}

        <TableContainer
          sx={{
            minHeight: "250px",
          }}
        >
          <Table
            sx={{
              padding: "16px",
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  "&:last-child td, &:last-child th": {
                    borderBottom: 1,
                    borderColor: "#E5E5E5",
                  },
                }}
              >
                <TableCell size="small" width={60}>
                  Ações
                </TableCell>

                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="judgmentTypeName"
                  label="Tipo de julgamento"
                  handleRequest={handlePagination}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />

                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="parentCategoryName"
                  label="Categoria Pai"
                  handleRequest={handlePagination}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />

                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="sequence"
                  label="Sequência"
                  handleRequest={handlePagination}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />

                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="categoryName"
                  label="Categoria"
                  handleRequest={handlePagination}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />

                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="quantityPlants"
                  label="Quantidade de plantas"
                  handleRequest={handlePagination}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                  disableOrder
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {judgments.length > 0 &&
                judgments.map(judgment => {
                  return (
                    <TableRow
                      key={judgment.id}
                      sx={{
                        "&:last-child td, &:last-child th": {
                          border: 0,
                        },
                      }}
                    >
                      <CustomMenuItem
                        id={judgment.id ? judgment.id : ""}
                        requestNumber={judgment.id}
                        handleToEdit={!finished ? handleToJudgment : undefined}
                        handleToDelete={!finished ? handleDeleteJudgment : undefined}
                        handleToNavigate={!finished ? handleToPlants : undefined}
                        navigateTitle="Cadastro de plantas"
                        handleToGenerateCertificates={handleGenerateCertificatesByJudgment}
                        finished={finished}
                      />

                      <CustomTableCell size="small" width={200}>
                        {judgment.judgmentType?.name}
                      </CustomTableCell>

                      <CustomTableCell size="small" width={400}>
                        {judgment.parentCategory?.name}
                      </CustomTableCell>

                      <CustomTableCell size="small" width={100}>
                        {judgment.category?.sequence}
                      </CustomTableCell>

                      <CustomTableCell size="small" width={400}>
                        {judgment.category?.name}
                      </CustomTableCell>

                      <CustomTableCell size="small" width={100}>
                        {judgment.plants?.length || 0}
                      </CustomTableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>

          {judgments.length === 0 && (
            <CustomNoContentTableBody description="Nenhum julgamento encontrado" />
          )}
        </TableContainer>
        <CustomTablePagination
          totalRows={totalRows}
          currentPage={currentPage}
          perPage={perPage}
          handleRequest={handlePagination}
          setPerPage={setPerPage}
          setCurrentPage={setCurrentPage}
          orderField={orderField}
          orderDirection={orderDirection}
        />

        {(loading || certificateLoading) && <BackdropCustom />}
      </PageCard>
    </>
  );
};
